import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getImageUrl } from '../utils/helpers';
import HtmlContent from '../components/HtmlContent';

const ActivityCategory = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { category, shortDescription, longDescription, image, filterCategory } = location.state || {};

  const handleViewSchedule = () => {
    navigate(`/activities/category/${encodeURIComponent(filterCategory || category)}`, {
      replace: true,
      state: { 
        fromCategoryPage: true,
        selectedCategory: filterCategory || category
      }
    });
  };

  if (!category) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" color="error">
          {t('categoryNotFound')}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2 }}>
        {/* Hero Image */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: 300, md: 500 },
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={getImageUrl(image)}
            alt={category}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
              pt: 8,
              pb: 4,
              px: { xs: 3, md: 6 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: 'common.white',
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              {category}
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 3, md: 6 } }}>
          <Stack spacing={4}>
            {/* Short Description Section */}
            <Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 3,
                }}
              >
                {t('introduction')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.8,
                  color: 'text.secondary',
                }}
              >
                {/* shortDescription stays plain text for cards; strip accidental tags if any */}
                {typeof shortDescription === 'string'
                  ? shortDescription.replace(/<[^>]+>/g, '')
                  : shortDescription}
              </Typography>
            </Box>

            <Divider />

            {/* Long Description Section */}
            <Box>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 3,
                }}
              >
                {t('detailedDescription')}
              </Typography>
              <HtmlContent
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.8,
                  color: 'text.secondary',
                }}
              >
                {longDescription}
              </HtmlContent>
            </Box>

            <Divider />

            {/* Schedule Section */}
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={handleViewSchedule}
                startIcon={<CalendarMonthIcon />}
                sx={{
                  py: { xs: 1.5, md: 2 },
                  px: { xs: 3, md: 4 },
                  fontSize: { xs: '1rem', md: '1.125rem' },
                }}
              >
                {t('viewSchedule')}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default ActivityCategory; 